import {
  GROUP_TYPE,
  MIN_MULTI_TREATMENT_GROUPS,
  SETUP_TOGGLES,
  validateVisitDrafts,
} from "./scheduleWorkflow";
import {
  fetchAdhocSetup,
  fetchAdhocWorksheets,
  fetchEcommTemplates,
  fetchStudyWorksheets,
  COMM_TEMPLATE_LABELS,
  fetchVisitPlannerDetails,
  getIncompleteCommTemplateLabels,
  hasCommTemplatesForPreview,
  mergeCommTemplates,
  mergePlannerRows,
  readPlannerSlotFields,
  readPlannerFinancialFields,
  VISIT_SLOT_DURATION_OPTIONS,
} from "./scheduleOptionalSetupService";
import {
  ADHOC_VISIT_KEY,
  isAdhocVisit,
  treatmentGroupNames,
  VISIT_TYPE,
  visitsForVisitRail,
} from "./scheduleVisitDraft";
import { validateVisitDraftDetails } from "./scheduleVisitPersistence";

export const CHECK_STATUS = {
  COMPLETE: "complete",
  PENDING: "pending",
  SKIPPED: "skipped",
  BLOCKED: "blocked",
};

function item(status, label, message = "", extra = {}) {
  return { status, label, message, ...extra };
}

export function isRequiredForLive(row) {
  return !!row && row.status !== CHECK_STATUS.SKIPPED && !row.optionalForLive;
}

function markOptionalForLive(rows = []) {
  return rows.map((row) => ({ ...row, optionalForLive: true }));
}

function formatPreviewValue(value) {
  if (value === undefined || value === null || value === "") return "—";
  return String(value);
}

function formatSignedPreviewValue(sign, value) {
  const formatted = formatPreviewValue(value);
  if (formatted === "—") return formatted;
  return `${sign}${formatted}`;
}

function formatVisitTimePointSummary(visit, { screening = false } = {}) {
  if (visit.visitType === VISIT_TYPE.SCREENING || visit.isScreening) {
    if (!screening) return "Screening visit (group-level window)";
    return [
      `Start: ${formatPreviewValue(visit.screeningWindowStart)}`,
      `Finish: ${formatPreviewValue(visit.screeningWindowFinish)}`,
      `Duration: ${formatPreviewValue(visit.screeningDuration)}`,
    ].join("\n");
  }
  if (visit.visitType === VISIT_TYPE.RESIDENTIAL) {
    return [
      `Start: ${formatPreviewValue(visit.startPeriod)}`,
      `Finish: ${formatPreviewValue(visit.finishPeriod)}`,
      `Duration: ${formatPreviewValue(visit.periodDuration)}`,
      `Min: ${formatSignedPreviewValue("-", visit.tolerencePeriod)}`,
      `Max: ${formatSignedPreviewValue("+", visit.upperTolerenceLimit)}`,
    ].join("\n");
  }
  return [
    `Day: ${formatPreviewValue(visit.nonResidVisitOccur)}`,
    `Min: ${formatSignedPreviewValue("-", visit.nonResidTolerencePeriod)}`,
    `Max: ${formatSignedPreviewValue("+", visit.nonResidUpperTolerenceLimit)}`,
  ].join("\n");
}

function formatPlannerSummary(planner) {
  const slot = readPlannerSlotFields(planner);
  if (!slot.hasSaved) return "";
  const durationLabel =
    VISIT_SLOT_DURATION_OPTIONS.find((opt) => opt.value === String(slot.slotDuration))
      ?.label || `${slot.slotDuration} min`;
  return `${slot.bookingPerSlot} booking(s) per slot · ${durationLabel}`;
}

function commEmailBodyPlainText(html = "") {
  return String(html || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function buildCommPreviewBlocks(templates = []) {
  return (templates || [])
    .filter((template) => template.IsSMS || template.IsEmail)
    .map((template) => {
      const typeKey = String(template.TemplateType || template.AppointmentType || "").trim();
      const typeLabel = COMM_TEMPLATE_LABELS[typeKey] || typeKey || "Template";
      return {
        typeLabel,
        isEmail: !!template.IsEmail,
        isSms: !!template.IsSMS,
        subject: String(template.EmailSubject || "").trim(),
        body: commEmailBodyPlainText(template.EmailText),
        sms: String(template.SMSText || "").trim(),
      };
    });
}

function formatCommTemplatesInlineSummary(templates = []) {
  const blocks = buildCommPreviewBlocks(templates);
  if (!blocks.length) return "No email/SMS templates enabled";
  return blocks
    .map((block) => {
      const channels = [block.isEmail && "Email", block.isSms && "SMS"]
        .filter(Boolean)
        .join(" + ");
      return `${block.typeLabel} (${channels})`;
    })
    .join("\n");
}

function commPendingMessage(templates, fallback = "") {
  const missing = getIncompleteCommTemplateLabels(templates);
  if (!missing.length) return fallback;
  return `Complete templates for: ${missing.join(", ")}.`;
}

function commChecklistItem(status, templates, pendingMessage = "") {
  const blocks = buildCommPreviewBlocks(templates);
  if (status === CHECK_STATUS.COMPLETE) {
    if (!blocks.length) {
      return item(CHECK_STATUS.COMPLETE, "E-communication", "No email/SMS templates enabled");
    }
    return item(
      CHECK_STATUS.COMPLETE,
      "E-communication",
      formatCommTemplatesInlineSummary(templates),
      { commTemplates: blocks },
    );
  }
  return item(CHECK_STATUS.PENDING, "E-communication", pendingMessage);
}

function formatDocumentsSummary(rows = []) {
  const names = (rows || [])
    .map(
      (row) =>
        row.WorkSheetName ||
        row.workSheetName ||
        row.FileName ||
        row.fileName ||
        row.DocumentName ||
        row.documentName,
    )
    .map((name) => String(name || "").trim())
    .filter(Boolean);
  if (!names.length) return "";
  return names.join(", ");
}

function formatPaymentSummary(planner, currencySymbol = "£") {
  const { payment, hasSavedPayment } = readPlannerFinancialFields(planner);
  if (!hasSavedPayment) return "";
  return `${currencySymbol}${payment}`;
}

function formatCostSummary(planner, visit, screeningRandomisationTogether, currencySymbol = "£") {
  const { visitCost, randomCost, hasSavedVisitCost, hasSavedRandomCost } =
    readPlannerFinancialFields(planner);
  const isScreeningVisit =
    visit.visitType === VISIT_TYPE.SCREENING || visit.isScreening;
  if (isScreeningVisit && screeningRandomisationTogether) {
    const parts = [];
    if (hasSavedVisitCost) parts.push(`Visit cost ${currencySymbol}${visitCost}`);
    if (hasSavedRandomCost) parts.push(`Randomisation cost ${currencySymbol}${randomCost}`);
    return parts.join(" · ");
  }
  if (!hasSavedVisitCost) return "";
  return `${currencySymbol}${visitCost}`;
}

function visitTypeKey(visit) {
  if (isAdhocVisit(visit)) return VISIT_TYPE.ADHOC;
  if (visit.visitType === VISIT_TYPE.SCREENING || visit.isScreening) return VISIT_TYPE.SCREENING;
  if (visit.visitType === VISIT_TYPE.RESIDENTIAL) return VISIT_TYPE.RESIDENTIAL;
  return VISIT_TYPE.NON_RESIDENTIAL;
}

function visitTypeLabel(visit) {
  if (isAdhocVisit(visit)) return "Adhoc";
  if (visit.visitType === VISIT_TYPE.SCREENING || visit.isScreening) return "Screening";
  if (visit.visitType === VISIT_TYPE.RESIDENTIAL) return "Residential";
  return "Non-res";
}

export function visitTypeBadgeLabel(visit) {
  if (isAdhocVisit(visit)) return "Adhoc";
  if (visit.visitType === VISIT_TYPE.SCREENING || visit.isScreening) return "Screen";
  if (visit.visitType === VISIT_TYPE.RESIDENTIAL) return "Res";
  return "Non-res";
}

export function visitTypeBadgeClass(typeKey) {
  switch (typeKey) {
    case VISIT_TYPE.SCREENING:
      return "is-screening";
    case VISIT_TYPE.RESIDENTIAL:
      return "is-res";
    case VISIT_TYPE.ADHOC:
      return "is-adhoc";
    default:
      return "is-non-res";
  }
}

function summarizeVisitItems(items = [], { forLive = false } = {}) {
  const required = items.filter((row) => {
    if (row.status === CHECK_STATUS.SKIPPED) return false;
    if (forLive && row.optionalForLive) return false;
    return true;
  });
  if (!required.length) return CHECK_STATUS.COMPLETE;
  if (required.every((row) => row.status === CHECK_STATUS.COMPLETE)) {
    return CHECK_STATUS.COMPLETE;
  }
  if (required.some((row) => row.status === CHECK_STATUS.BLOCKED)) {
    return CHECK_STATUS.BLOCKED;
  }
  return CHECK_STATUS.PENDING;
}

export function buildVisitGroupsForPreview({
  wizard,
  visitBuilderMode,
  visitCohorts,
}) {
  if (visitBuilderMode === "cohorts" && visitCohorts?.length) {
    return visitCohorts.map((group) => ({
      groupId: group.id,
      groupName: group.name,
      visits: visitsForVisitRail(group.visits),
    }));
  }
  return [
    {
      groupId: wizard.groupNames?.[0]?.GroupNameId || wizard.groupNames?.[0]?.groupNameId || null,
      groupName: wizard.groupType === GROUP_TYPE.MULTIPLE ? "Shared schedule" : "Visits",
      visits: visitsForVisitRail(wizard.visitDrafts),
    },
  ];
}

export function computeScheduleSetupChecks({
  wizard,
  cohort,
  scheduleCreated,
}) {
  const items = [];
  const studyScheduleId = cohort?.studyScheduleId;

  items.push(
    scheduleCreated && studyScheduleId
      ? item(CHECK_STATUS.COMPLETE, "Schedule setup saved")
      : item(CHECK_STATUS.PENDING, "Schedule setup saved", "Complete and save the Setup step."),
  );

  items.push(
    item(
      CHECK_STATUS.COMPLETE,
      `Group type: ${wizard.groupType}`,
      wizard.groupType === GROUP_TYPE.MULTIPLE
        ? `${treatmentGroupNames(wizard.groupNames || []).length} treatment group(s)`
        : "Single group study",
    ),
  );

  SETUP_TOGGLES.forEach((toggle) => {
    if (toggle.dependsOn && !wizard.setupFlags[toggle.dependsOn]) return;
    const enabled = !!wizard.setupFlags[toggle.key];
    items.push(
      item(
        CHECK_STATUS.COMPLETE,
        toggle.label,
        enabled
          ? toggle.key === "studyWorksheet"
            ? "Enabled — optional for go-live."
            : "Enabled — must be completed per visit."
          : "Not enabled",
      ),
    );
  });

  if (wizard.groupType === GROUP_TYPE.MULTIPLE) {
    const names = treatmentGroupNames(wizard.groupNames || []);
    const namedCount = names.length;
    const hasIds = names.every(
      (g) => g.GroupNameId || g.groupNameId || g.GroupNameIdRef || g.groupNameIdRef,
    );
    const hasNames = names.every((g) =>
      String(g.GroupName || g.groupName || "").trim(),
    );

    items.push(
      namedCount >= MIN_MULTI_TREATMENT_GROUPS
        ? item(
            CHECK_STATUS.COMPLETE,
            "Treatment groups configured",
            `${namedCount} group(s) added`,
          )
        : item(
            CHECK_STATUS.PENDING,
            "Treatment groups configured",
            `Add at least ${MIN_MULTI_TREATMENT_GROUPS} groups using + in the group selector.`,
          ),
    );

    items.push(
      hasIds && hasNames && namedCount >= MIN_MULTI_TREATMENT_GROUPS
        ? item(CHECK_STATUS.COMPLETE, "Group names saved")
        : item(
            CHECK_STATUS.PENDING,
            "Group names saved",
            "Add and save group names on the Groups & Visits step.",
          ),
    );
  }

  return items;
}

export function computeVisitStructureChecks({ wizard, visitGroups }) {
  const screening = wizard.setupFlags.studyScreening;
  const items = [];

  const allVisits = visitGroups.flatMap((g) => g.visits);
  if (!allVisits.length) {
    items.push(item(CHECK_STATUS.PENDING, "Visits configured", "Add at least one visit."));
    return { items, visitGroups, allVisits };
  }

  visitGroups.forEach((group) => {
    const regularVisits = group.visits.filter((visit) => !isAdhocVisit(visit));
    const structureCheck = validateVisitDrafts(regularVisits, { screening });
    items.push(
      structureCheck.valid
        ? item(
            CHECK_STATUS.COMPLETE,
            `${group.groupName}: visit structure`,
            `${regularVisits.length} visit(s) with valid names and order`,
          )
        : item(
            CHECK_STATUS.PENDING,
            `${group.groupName}: visit structure`,
            structureCheck.message,
          ),
    );

    const missingDb = regularVisits.some((visit) => !visit.visitsPerStudyId);
    items.push(
      missingDb
        ? item(
            CHECK_STATUS.BLOCKED,
            `${group.groupName}: visits saved to database`,
            "Open the Groups & Visits step and wait for visits to finish saving.",
          )
        : item(CHECK_STATUS.COMPLETE, `${group.groupName}: visits saved to database`),
    );
  });

  return { items, visitGroups, allVisits };
}

function buildLocalVisitItems(visit, setupFlags, { screening = false } = {}) {
  const items = [];
  const label = visit.visitName || "Visit";

  if (!visit.visitsPerStudyId) {
    items.push(
      item(CHECK_STATUS.BLOCKED, "Visit saved", "Visit must exist in the database before optional setup."),
    );
    return items;
  }

  const timePointCheck = validateVisitDraftDetails(visit, { screening });
  const timePointSummary = formatVisitTimePointSummary(visit, { screening });
  items.push(
    timePointCheck.valid
      ? item(CHECK_STATUS.COMPLETE, "Visit time point", timePointSummary)
      : item(CHECK_STATUS.PENDING, "Visit time point", timePointCheck.message),
  );

  if (setupFlags.visitPayment) {
    items.push(item(CHECK_STATUS.PENDING, "Visit payment", "Checking saved payment…"));
  }
  if (setupFlags.visitCost) {
    items.push(item(CHECK_STATUS.PENDING, "Visit cost", "Checking saved cost…"));
  }
  if (setupFlags.eCommunication) {
    items.push(item(CHECK_STATUS.PENDING, "E-communication", "Checking templates…"));
  }
  if (setupFlags.studyWorksheet) {
    items.push(
      item(CHECK_STATUS.PENDING, "Documents", "Checking documents…", {
        optionalForLive: true,
      }),
    );
  }

  return items.map((row) => ({ ...row, visitLabel: label }));
}

async function fetchAdhocOptionalItems({ setupFlags, ctx }) {
  const items = [];
  const needsSetup =
    setupFlags.visitPayment ||
    setupFlags.visitCost ||
    setupFlags.eCommunication;

  let adhocData = null;
  if (needsSetup) {
    try {
      adhocData = await fetchAdhocSetup(ctx);
    } catch {
      adhocData = null;
    }
  }

  if (setupFlags.visitPayment) {
    const payment = Number(adhocData?.payment);
    items.push(
      Number.isFinite(payment) && payment > 0
        ? item(CHECK_STATUS.COMPLETE, "Visit payment", `£${payment}`)
        : item(
            CHECK_STATUS.PENDING,
            "Visit payment",
            adhocData
              ? "Enter adhoc payment amount greater than zero."
              : "Could not verify adhoc payment.",
          ),
    );
  }

  if (setupFlags.visitCost) {
    const cost = Number(adhocData?.visitCost);
    items.push(
      Number.isFinite(cost) && cost > 0
        ? item(CHECK_STATUS.COMPLETE, "Visit cost", `£${cost}`)
        : item(
            CHECK_STATUS.PENDING,
            "Visit cost",
            adhocData
              ? "Enter adhoc cost amount greater than zero."
              : "Could not verify adhoc cost.",
          ),
    );
  }

  if (setupFlags.eCommunication) {
    const templates = mergeCommTemplates(adhocData?.templates || []);
    items.push(
      hasCommTemplatesForPreview(templates)
        ? commChecklistItem(CHECK_STATUS.COMPLETE, templates)
        : commChecklistItem(
            CHECK_STATUS.PENDING,
            templates,
            adhocData
              ? commPendingMessage(templates, "Complete enabled SMS or email templates.")
              : "Could not verify adhoc communication templates.",
          ),
    );
  }

  if (setupFlags.studyWorksheet) {
    try {
      const rows = await fetchAdhocWorksheets(ctx);
      const docSummary = formatDocumentsSummary(rows);
      items.push(
        Array.isArray(rows) && rows.length
          ? item(CHECK_STATUS.COMPLETE, "Documents", docSummary || `${rows.length} document(s)`)
          : item(CHECK_STATUS.PENDING, "Documents", "Upload at least one adhoc document."),
      );
    } catch {
      items.push(item(CHECK_STATUS.PENDING, "Documents", "Could not verify adhoc documents."));
    }
  }

  if (!items.length) {
    items.push(item(CHECK_STATUS.COMPLETE, "Adhoc visit", "No optional setup enabled for adhoc."));
  }

  return markOptionalForLive(items);
}

async function fetchVisitOptionalItems({
  visit,
  setupFlags,
  ctx,
  screening,
  screeningRandomisationTogether,
}) {
  const items = [];
  const visitId = visit.visitsPerStudyId;
  if (!visitId) {
    return buildLocalVisitItems(visit, setupFlags, { screening });
  }

  const timePointCheck = validateVisitDraftDetails(visit, { screening });
  const timePointSummary = formatVisitTimePointSummary(visit, { screening });
  items.push(
    timePointCheck.valid
      ? item(CHECK_STATUS.COMPLETE, "Visit time point", timePointSummary)
      : item(CHECK_STATUS.PENDING, "Visit time point", timePointCheck.message),
  );

  let planner = null;
  let commTemplates = [];

  const needsPlanner = setupFlags.visitPayment || setupFlags.visitCost || true;
  const currencySymbol = ctx.currencySymbol || "£";

  if (needsPlanner || setupFlags.eCommunication) {
    try {
      if (setupFlags.eCommunication) {
        const ecomm = await fetchEcommTemplates({ ...ctx, visitId, groupId: ctx.groupId });
        commTemplates = mergeCommTemplates(ecomm.templates);
        items.push(
          hasCommTemplatesForPreview(commTemplates)
            ? commChecklistItem(CHECK_STATUS.COMPLETE, commTemplates)
            : commChecklistItem(
                CHECK_STATUS.PENDING,
                commTemplates,
                commPendingMessage(
                  commTemplates,
                  "Complete enabled SMS or email templates.",
                ),
              ),
        );
      }
      if (needsPlanner) {
        const details = await fetchVisitPlannerDetails({
          ...ctx,
          visitId,
          plannerFallback: visit.plannerSnapshot,
        });
        planner = mergePlannerRows(details.planner, visit.plannerSnapshot);
      }
    } catch {
      if (setupFlags.eCommunication) {
        items.push(
          item(CHECK_STATUS.PENDING, "E-communication", "Could not verify communication templates."),
        );
      }
      if (needsPlanner) {
        items.push(
          item(CHECK_STATUS.PENDING, "Planner setup", "Could not verify planner setup."),
        );
      }
      if (setupFlags.visitPayment) {
        items.push(
          item(CHECK_STATUS.PENDING, "Visit payment", "Could not verify saved payment."),
        );
      }
      if (setupFlags.visitCost) {
        items.push(item(CHECK_STATUS.PENDING, "Visit cost", "Could not verify saved cost."));
      }
    }
  }

  if (!items.some((entry) => entry.label === "Planner setup")) {
    const slot = readPlannerSlotFields(planner);
    const plannerSummary = formatPlannerSummary(planner);
    items.push(
      slot.hasSaved
        ? item(CHECK_STATUS.COMPLETE, "Planner setup", plannerSummary)
        : item(
            CHECK_STATUS.PENDING,
            "Planner setup",
            "Set booking per slot and slot duration.",
          ),
    );
  }

  if (setupFlags.visitPayment) {
    const paymentSummary = formatPaymentSummary(planner, currencySymbol);
    const payment = Number(planner?.VisitPayment ?? planner?.visitPayment);
    items.push(
      Number.isFinite(payment) && payment > 0
        ? item(CHECK_STATUS.COMPLETE, "Visit payment", paymentSummary)
        : item(CHECK_STATUS.PENDING, "Visit payment", "Enter a payment amount greater than zero."),
    );
  }

  if (setupFlags.visitCost) {
    const isScreeningVisit =
      visit.visitType === VISIT_TYPE.SCREENING || visit.isScreening;
    const costOk = Number(planner?.VisitCost ?? planner?.visitCost) > 0;
    const costSummary = formatCostSummary(
      planner,
      visit,
      screeningRandomisationTogether,
      currencySymbol,
    );
    if (isScreeningVisit && screeningRandomisationTogether) {
      const randomOk = Number(planner?.RandomCost ?? planner?.randomCost) > 0;
      items.push(
        costOk && randomOk
          ? item(CHECK_STATUS.COMPLETE, "Visit cost & randomisation cost", costSummary)
          : item(
              CHECK_STATUS.PENDING,
              "Visit cost & randomisation cost",
              "Enter visit cost and randomisation cost.",
            ),
      );
    } else {
      items.push(
        costOk
          ? item(CHECK_STATUS.COMPLETE, "Visit cost", costSummary)
          : item(CHECK_STATUS.PENDING, "Visit cost", "Enter a cost amount greater than zero."),
      );
    }
  }

  if (setupFlags.studyWorksheet) {
    try {
      const rows = await fetchStudyWorksheets({ ...ctx, visitId, groupId: ctx.groupId });
      const docSummary = formatDocumentsSummary(rows);
      items.push(
        Array.isArray(rows) && rows.length
          ? item(CHECK_STATUS.COMPLETE, "Documents", docSummary || `${rows.length} document(s)`, {
              optionalForLive: true,
            })
          : item(CHECK_STATUS.PENDING, "Documents", "Upload at least one visit document.", {
              optionalForLive: true,
            }),
      );
    } catch {
      items.push(
        item(CHECK_STATUS.PENDING, "Documents", "Could not verify visit documents.", {
          optionalForLive: true,
        }),
      );
    }
  }

  return items;
}

export async function buildSchedulePreviewReport({
  wizard,
  cohort,
  scheduleCreated,
  visitBuilderMode,
  visitCohorts,
  studyScheduleId,
  organizationId,
  createdBy,
}) {
  const setupItems = computeScheduleSetupChecks({ wizard, cohort, scheduleCreated });
  const visitGroups = buildVisitGroupsForPreview({ wizard, visitBuilderMode, visitCohorts });
  const { items: visitStructureItems } = computeVisitStructureChecks({ wizard, visitGroups });

  const setupFlags = wizard.setupFlags;
  const screening = setupFlags.studyScreening;
  const ctxBase = {
    studyScheduleId,
    organizationId,
    createdBy,
    currencySymbol: cohort?.currencySymbol || "£",
  };

  const visitReports = [];
  for (const group of visitGroups) {
    for (const visit of group.visits) {
      const adhoc = isAdhocVisit(visit);
      const items = adhoc
        ? await fetchAdhocOptionalItems({
            setupFlags,
            ctx: {
              ...ctxBase,
              groupId: group.groupId,
            },
          })
        : await fetchVisitOptionalItems({
            visit,
            setupFlags,
            screening,
            screeningRandomisationTogether: setupFlags.screeningRandomisationTogether,
            ctx: {
              ...ctxBase,
              visitId: visit.visitsPerStudyId,
              groupId: group.groupId,
            },
          });
      visitReports.push({
        groupId: group.groupId,
        groupName: group.groupName,
        visitId: adhoc ? ADHOC_VISIT_KEY : visit.visitsPerStudyId,
        visitName: visit.visitName || "Visit",
        visitType: visitTypeLabel(visit),
        visitTypeBadge: visitTypeBadgeLabel(visit),
        visitTypeKey: visitTypeKey(visit),
        isAdhoc: adhoc,
        status: summarizeVisitItems(items),
        liveStatus: summarizeVisitItems(items, { forLive: true }),
        items,
      });
    }
  }

  const flatItems = [...setupItems, ...visitStructureItems];
  visitReports.forEach((visit) => flatItems.push(...visit.items));

  const actionable = flatItems.filter((row) => row.status !== CHECK_STATUS.SKIPPED);
  const completeCount = actionable.filter((row) => row.status === CHECK_STATUS.COMPLETE).length;
  const pendingCount = actionable.filter((row) => row.status === CHECK_STATUS.PENDING).length;
  const blockedCount = actionable.filter((row) => row.status === CHECK_STATUS.BLOCKED).length;
  const requiredItems = actionable.filter(isRequiredForLive);
  const requiredComplete = requiredItems.filter((row) => row.status === CHECK_STATUS.COMPLETE).length;
  const requiredPending = requiredItems.filter((row) => row.status === CHECK_STATUS.PENDING).length;
  const requiredBlocked = requiredItems.filter((row) => row.status === CHECK_STATUS.BLOCKED).length;
  const optionalPending = actionable.filter(
    (row) => row.optionalForLive && row.status === CHECK_STATUS.PENDING,
  ).length;

  const requiredOptionalFlags = [
    setupFlags.visitPayment && "payment",
    setupFlags.visitCost && "cost",
    setupFlags.eCommunication && "communication",
  ].filter(Boolean);

  return {
    summary: {
      total: actionable.length,
      complete: completeCount,
      pending: pendingCount,
      blocked: blockedCount,
      requiredTotal: requiredItems.length,
      requiredComplete,
      requiredPending,
      requiredBlocked,
      optionalPending,
      readyForLive:
        requiredBlocked === 0 &&
        requiredPending === 0 &&
        visitStructureItems.every((row) => row.status === CHECK_STATUS.COMPLETE) &&
        visitReports.every((visit) => visit.liveStatus === CHECK_STATUS.COMPLETE),
      enabledOptional: requiredOptionalFlags,
    },
    setupItems,
    visitStructureItems,
    visitGroups: visitGroups.map((group) => ({
      ...group,
      visits: visitReports.filter((visit) => visit.groupId === group.groupId),
    })),
    visitReports,
  };
}
